import { use, useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router";
import { logger, sanitizeError } from "../utils/logger.js";
import { AppContext } from "../context/AppContext";
import { useNavigation } from "../context/NavigationContext";
import {
  filterBuyerCompanies,
  getBuyerCompanies,
  paginateBuyerCompanies,
  sortBuyerCompanies,
} from "../logic/buyerCompany";
import { getUserRoleData } from "../logic/user";
import { canAccessBuyerCompanies } from "../utils/accessControl";
import useRequireAccess from "./useRequireAccess";

/** Default number of buyer companies shown on one page. */
const DEFAULT_RESULTS_LIMIT = 10;
const FIRST_PAGE = 1;
const SORT_FIELD_NAME = "nombre";
const SORT_ASCENDING = "ascending";
const SORT_DESCENDING = "descending";

/**
 * Loads, sorts, filters, and pages the buyer-companies list.
 * @returns {Object} List state and the handlers the page passes to its table.
 */
export function useBuyerCompaniesPage() {
  const navigate = useNavigate();
  const { allowed } = useRequireAccess(canAccessBuyerCompanies);
  const { changePermissions } = use(AppContext);
  const { updateNavigation } = useNavigation();
  const [buyerCompaniesData, setBuyerCompaniesData] = useState([]);
  const [sortedBuyerCompaniesList, setSortedBuyerCompaniesList] = useState([]);
  const [sortConfig, setSortConfig] = useState({
    key: SORT_FIELD_NAME,
    direction: SORT_ASCENDING,
  });
  const [searchQuery, setSearchQuery] = useState("");
  const [resultsLimit, setResultsLimit] = useState(DEFAULT_RESULTS_LIMIT);
  const [currentPage, setCurrentPage] = useState(FIRST_PAGE);
  const [loading, setLoading] = useState(true);

  /**
   * Loads the signed-in user's permissions and the buyer-company list.
   * @returns {Promise<void>}
   */
  const getData = useCallback(async () => {
    try {
      const permissions = await getUserRoleData(
        localStorage.getItem("userId"),
        navigate,
      );
      if (permissions) {
        changePermissions(permissions);
      }
      const buyerCompanies = await getBuyerCompanies(navigate);
      setBuyerCompaniesData(buyerCompanies);
    } catch (error) {
      logger.error("Error", { err: sanitizeError(error) });
    }
  }, [changePermissions, navigate]);

  useEffect(() => {
    /**
     * Fetches companies when a session token is present, then clears the loading flag.
     * @returns {Promise<void>}
     */
    const fetchData = async () => {
      try {
        const token = localStorage.getItem("token");
        if (!!token && token.trim() !== "undefined") {
          await getData();
        } else {
          logger.info(
            "Error trying to get the token to retrieve the users data.",
          );
        }
      } catch (error) {
        logger.error("Error during initial data fetch:", {
          err: sanitizeError(error),
        });
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  useEffect(() => {
    updateNavigation("/buyerCompanies", "buyerCompanies");
  }, [updateNavigation]);

  useEffect(() => {
    const sortedList = sortBuyerCompanies(buyerCompaniesData, sortConfig);
    setSortedBuyerCompaniesList(sortedList);
  }, [buyerCompaniesData, sortConfig]);

  const filteredBuyerCompaniesList = filterBuyerCompanies(
    sortedBuyerCompaniesList,
    searchQuery,
  );
  const paginatedBuyerCompaniesList = paginateBuyerCompanies(
    filteredBuyerCompaniesList,
    currentPage,
    resultsLimit,
  );

  /**
   * Toggles the company-name sort between ascending and descending.
   * @returns {void}
   */
  const handleSort = useCallback(() => {
    setSortConfig((current) => ({
      key: SORT_FIELD_NAME,
      direction:
        current.direction === SORT_ASCENDING ? SORT_DESCENDING : SORT_ASCENDING,
    }));
  }, []);

  /**
   * Stores the search box text used to filter the list.
   * @param {React.ChangeEvent<HTMLInputElement>} event
   * @returns {void}
   */
  const handleSearch = useCallback((event) => {
    setSearchQuery(event.target.value);
  }, []);

  /**
   * Applies a new page size and returns to the first page.
   * @param {{value: number}} selectedOption - Results-per-page option from the dropdown.
   * @returns {void}
   */
  const handleResultsLimitChange = useCallback((selectedOption) => {
    setResultsLimit(selectedOption.value);
    setCurrentPage(FIRST_PAGE);
  }, []);

  /**
   * Moves one page forward or back when that page exists.
   * @param {"next"|"prev"} direction
   * @returns {void}
   */
  const handlePageChange = useCallback(
    (direction) => {
      const totalPages = Math.ceil(
        filteredBuyerCompaniesList.length / resultsLimit,
      );
      if (direction === "next" && currentPage < totalPages) {
        setCurrentPage((prevPage) => prevPage + 1);
      } else if (direction === "prev" && currentPage > FIRST_PAGE) {
        setCurrentPage((prevPage) => prevPage - 1);
      } else {
        // Other directions, or next/prev at the ends of the list, leave the page unchanged.
      }
    },
    [currentPage, filteredBuyerCompaniesList.length, resultsLimit],
  );

  /**
   * Opens the buyer-company form in create mode.
   * @returns {void}
   */
  const handleCreateBuyerCompany = useCallback(() => {
    navigate("/buyerCompanyForm", {
      state: {
        isEditMode: false,
      },
    });
  }, [navigate]);

  return {
    navigate,
    allowed,
    loading,
    searchQuery,
    sortConfig,
    currentPage,
    resultsLimit,
    filteredBuyerCompaniesList,
    paginatedBuyerCompaniesList,
    handleSort,
    handleSearch,
    handleResultsLimitChange,
    handlePageChange,
    handleCreateBuyerCompany,
  };
}
